import React, { useState } from 'react';
import { Tabs, Tab, Box, Typography, Autocomplete, TextField, Button } from '@mui/material';
import Download from '@mui/icons-material/Download';
import CheckCircle from '@mui/icons-material/CheckCircle';
import SpinnerModal from './SpinnerModal';

const dbOptions = ['MySQL', 'Oracle', 'Postgres'];

const RdbmsToRdbms = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [sourceDb, setSourceDb] = useState('');
  const [targetDb, setTargetDb] = useState('');
  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [schemaFile, setSchemaFile] = useState(null);
  const [submitApiResult, setSubmitApiResult] = useState(null);
  const [mappingFileName, setMappingFileName] = useState('');
  const [generateApiResult, setGenerateApiResult] = useState(null);
  const [dataInsertionResult, setDataInsertionResult] = useState(null);
  const [showSpinnerModal, setShowSpinnerModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const [spinnerType, setSpinnerType] = useState(null);


  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setSchemaFile(file);
  };

  const handleSubmit = async () => {
    if (!schemaFile || !sourceDb || !targetDb) {
      alert("Please select source/target DB and upload a schema file.");
      return;
    }
    try {
      setSpinnerType('mapping');
      setShowSpinnerModal(true);
      setProgress(0);

      // Simulate progress
      let interval=null;
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 800);

      // 1. Upload schema file
      const formData = new FormData();
      formData.append("file", schemaFile);

      const uploadRes = await fetch("https://api.elevate.de.ltim.io/file_upload_for_rdbms_to_rdbms/", {
        method: "POST",
        body: formData,
      });

      if (!uploadRes.ok) {
        clearInterval(interval);
        setShowSpinnerModal(false);
        throw new Error("Schema file upload failed");
      }

      // 2. Call rdbms_to_rdbms endpoint
      const payload = {
        src_backup_file_name: schemaFile.name,
        mapping_file_name: "",
        source_db_name: sourceDb,
        target_db_name: targetDb,
        is_src_backup_file: true,
        is_mapping_file: false,
        is_insertion: false,
      };

      const res = await fetch("https://api.elevate.de.ltim.io/rdbms_to_rdbms/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      clearInterval(interval);

      if (!res.ok) {
        setShowSpinnerModal(false);
        throw new Error("Processing failed");
      }

      const data = await res.json();
      setSubmitApiResult(data);
      setProgress(100);
      setTimeout(() => {
        setShowSpinnerModal(false);
        setTabIndex(1);
      }, 1500);
    } catch (err) {
      setShowSpinnerModal(false);
      alert(err.message || "Something went wrong");
    }
  };

  const handleDownloadMapping = async () => {
    if (!submitApiResult || !submitApiResult.links || submitApiResult.links.length === 0) {
      alert('No mapping links available to download.');
      return;
    }

    submitApiResult.links.forEach((link) => {
      const anchor = document.createElement('a');
      anchor.href = link;
      anchor.target = '_self';
      anchor.download = '';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    });

  };

  const handleFileUploadCsv = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      alert('Please select a file to upload.');
      return;
    }

    setMappingFileName(file.name);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadResponse = await fetch('https://api.elevate.de.ltim.io/file_upload_for_rdbms_to_rdbms/', {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        alert('Failed to upload the updated file.');
        return;
      }

    } catch (error) {
      console.error('Error uploading updated file:', error);
      alert('An error occurred while uploading the updated file.');
    }
  };

  const handleGenerateResult = async () => {
    if (!mappingFileName) {
      alert("Please upload the reviewed mapping CSV.");
      return;
    }
    setSpinnerType('ddl');
    setShowSpinnerModal(true);
    setProgress(0);

    // Simulate progress
    let interval = null;
    interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 30000);

    try {
      const payload = {
        src_backup_file_name: "",
        mapping_file_name: mappingFileName,
        source_db_name: "",
        target_db_name: "",
        is_src_backup_file: false,
        is_mapping_file: true,
        is_insertion: false,
      };

      const res = await fetch("https://api.elevate.de.ltim.io/rdbms_to_rdbms/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      clearInterval(interval);

      if (!res.ok) {
        setShowSpinnerModal(false);
        throw new Error("Failed to generate DDL");
      }

      const data = await res.json();
      setGenerateApiResult(data);
      setProgress(100);
      setTimeout(() => {
        setShowSpinnerModal(false);
        setTabIndex(2);
      }, 1500);
    } catch (err) {
      setShowSpinnerModal(false);
      alert(err.message || "Something went wrong");
    }
  };

  const handleDownloadDDL = async () => {
    if (!generateApiResult || !generateApiResult.links || generateApiResult.links.length === 0) {
      alert('No mapping links available to download.');
      return;
    }

    generateApiResult.links.forEach((link) => {
      const anchor = document.createElement('a');
      anchor.href = link;
      anchor.target = '_self';
      anchor.download = '';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    });

  };

  const handleDownloadInsertionScripts = async () => {
    if (!dataInsertionResult || !dataInsertionResult.links || dataInsertionResult.links.length === 0) {
      alert('No mapping links available to download.');
      return;
    }

    dataInsertionResult.links.forEach((link) => {
      const anchor = document.createElement('a');
      anchor.href = link;
      anchor.target = '_self';
      anchor.download = '';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    });

  };

  const handleDataInsertion = async () => {
    setSpinnerType('insertion');
    setShowSpinnerModal(true);
    setProgress(0);

    // Simulate progress
    let interval = null;
    interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 500);

    try {
      const payload = {
        src_backup_file_name: "",
        mapping_file_name: "",
        source_db_name: "",
        target_db_name : "",
        is_src_backup_file: false,
        is_mapping_file: false,
        is_insertion: true,
      };

      const res = await fetch("https://api.elevate.de.ltim.io/rdbms_to_rdbms/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      clearInterval(interval);

      if (!res.ok) {
        setShowSpinnerModal(false);
        throw new Error("Failed to perform data insertion");
      }

      const data = await res.json();
      setDataInsertionResult(data);
      setProgress(100);
      setTimeout(() => {
        setShowSpinnerModal(false);
        setTabIndex(3);
      }, 1500);
    } catch (err) {
      setShowSpinnerModal(false);
      alert(err.message || "Something went wrong");
    }
  };

  const getSpinnerMessages = () => {
    switch (spinnerType) {
      case 'mapping':
        return {
          inProgress: 'Mapping is being generated...',
          success: 'Mapping generated Successfully!',
          open: showSpinnerModal,
          onClose: () => setShowSpinnerModal(false),
        };
      case 'ddl':
        return {
          inProgress: 'DDL is being generated...',
          success: 'DDL Generated Successfully!',
          open: showSpinnerModal,
          onClose: () => setShowSpinnerModal(false),
        };
      case 'insertion':
        return {
          inProgress: 'Script is being generated...',
          success: 'Script Generated Successfully!',
          open: showSpinnerModal,
          onClose: () => setShowSpinnerModal(false),
        };
      default:
        return {};
    }
  };
  const spinnerMessages = getSpinnerMessages();

  return (
    <Box sx={{ width: '100%', marginTop: -7 }}>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{
          pl: 0,
          '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
          '& .MuiTab-root': {
            minWidth: 120,
            fontWeight: 'bold',
            textTransform: 'none',
            paddingRight: 0,
            pl: 0,
          },
        }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        {/* Input Tab */}
        <Tab
          label={
            <Box display="flex" alignItems="center">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={submitApiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "white")}
                border="1.5px solid"
                borderColor={submitApiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "black")}
                mr={1}
                transition="all 0.2s"
              >
                {submitApiResult?.report ? (
                  <CheckCircle sx={{ color: "white", fontSize: 20 }} />
                ) : (
                  <Typography variant="subtitle2" sx={{ color: tabIndex === 0 ? "white" : "black", fontWeight: 600 }}>1</Typography>
                )}
              </Box>
              <Typography
                sx={{
                  fontWeight: tabIndex === 0 ? 700 : 400,
                  color: tabIndex === 0 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                Input
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 4,
                  ml: 1,
                  mr: 1,
                  mb: 0.6,
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                  &rarr;
                </Typography>
              </Box>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingRight: 0, paddingLeft: 0 }}
        />
        {/* Mapping Tab */}
        <Tab
          label={
            <Box display="flex" alignItems="center">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={generateApiResult?.report ? "#4caf50" : (tabIndex === 1 ? "#1976d2" : "white")}
                border="1.5px solid"
                borderColor={generateApiResult?.report ? "#4caf50" : (tabIndex === 1 ? "#1976d2" : "black")}
                mr={1}
                transition="all 0.2s"
              >
                {generateApiResult?.report ? (
                  <CheckCircle sx={{ color: "white", fontSize: 20 }} />
                ) : (
                  <Typography variant="subtitle2" sx={{ color: tabIndex === 1 ? "white" : "black", fontWeight: 600 }}>2</Typography>
                )}
              </Box>
              <Typography
                sx={{
                  fontWeight: tabIndex === 1 ? 700 : 400,
                  color: tabIndex === 1 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                Mapping
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 4,
                  ml: 1,
                  mr: 1,
                  mb: 0.6,
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                  &rarr;
                </Typography>
              </Box>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingLeft: 0, paddingRight: 0 }}
        />
        {/* DDL Tab */}
        <Tab
          label={
            <Box display="flex" alignItems="center">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={dataInsertionResult?.report ? "#4caf50" : (tabIndex === 2 ? "#1976d2" : "white")}
                border="1.5px solid"
                borderColor={dataInsertionResult?.report ? "#4caf50" : (tabIndex === 2 ? "#1976d2" : "black")}
                mr={1}
                transition="all 0.2s"
              >
                {dataInsertionResult?.report ? (
                  <CheckCircle sx={{ color: "white", fontSize: 20 }} />
                ) : (
                  <Typography variant="subtitle2" sx={{ color: tabIndex === 2 ? "white" : "black", fontWeight: 600 }}>3</Typography>
                )}
              </Box>
              <Typography
                sx={{
                  fontWeight: tabIndex === 2 ? 700 : 400,
                  color: tabIndex === 2 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                DDL
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 4,
                  ml: 1,
                  mr: 1,
                  mb: 0.6,
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                  &rarr;
                </Typography>
              </Box>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingLeft: 0 }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="center">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={dataInsertionResult?.report ? "#4caf50" : (tabIndex === 3 ? "#1976d2" : "white")}
                border="1.5px solid"
                borderColor={dataInsertionResult?.report ? "#4caf50" : (tabIndex === 3 ? "#1976d2" : "black")}
                mr={1}
                transition="all 0.2s"
              >
                {dataInsertionResult?.report ? (
                  <CheckCircle sx={{ color: "white", fontSize: 20 }} />
                ) : (
                  <Typography variant="subtitle2" sx={{ color: tabIndex === 3 ? "white" : "black", fontWeight: 600 }}>4</Typography>
                )}
              </Box>
              <Typography
                sx={{
                  fontWeight: tabIndex === 3 ? 700 : 400,
                  color: tabIndex === 3 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                Insertion
              </Typography>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingLeft: 0, paddingRight: 0 }}
        />
      </Tabs>

      {/* Tab Content */}
      {tabIndex === 0 && (
        <Box
          sx={{
            marginTop: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
            gap: 2,
          }}
        >
          <Box sx={{ maxWidth: 300 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Source DB<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Autocomplete
              options={dbOptions}
              value={sourceDb}
              onChange={(event, newValue) => setSourceDb(newValue || '')}
              inputValue={sourceSearch}
              onInputChange={(event, newInputValue) => setSourceSearch(newInputValue)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Source DB" variant="outlined" size="small" />
              )}
              sx={{ width: 300 }}
            />

            <Typography variant="subtitle1" sx={{ marginTop: 2, fontWeight: 'bold', marginBottom: 1 }}>
              Please upload schema backup file<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Button
              variant="contained"
              color="primary"
              component="label"
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
                height: '40px',
              }}
            >
              Upload File
              <input
                type="file"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
            {schemaFile && (
              <Typography variant="body2" sx={{ marginTop: 1, color: 'green', fontWeight: 'bold' }}>
                Uploaded: {schemaFile.name}
              </Typography>
            )}
            {schemaFile && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{
                  marginTop: 2,
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                  height: '40px',
                }}
              >
                Generate Mapping
              </Button>
            )}
          </Box>
          <Box sx={{ maxWidth: 300, marginRight: '150px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Target DB<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Autocomplete
              options={dbOptions}
              value={targetDb}
              onChange={(event, newValue) => setTargetDb(newValue || '')}
              inputValue={targetSearch}
              onInputChange={(event, newInputValue) => setTargetSearch(newInputValue)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Target DB" variant="outlined" size="small" />
              )}
              sx={{ width: 300 }}
            />
          </Box>
        </Box>
      )}
      {tabIndex === 1 && (
        <Box sx={{ marginTop: 2 }}>
          <Box
            sx={{
              marginTop: 4,
              padding: '16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginBottom: 2,
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Output
            </Typography>
            {submitApiResult?.report ? (
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
              >
                {String(submitApiResult.report)}
              </Typography>
            ) : (
              <Typography variant="body2">No result available</Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color: '#333',
                marginBottom: 1,
              }}
            >
              Please Download and Review the Mapping CSV
            </Typography>


            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              {submitApiResult?.links?.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDownloadMapping}
                  sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid black',
                    boxShadow: 'none',
                  }}
                  startIcon={<Download />}
                >
                  Download Mapping
                </Button>
              )}

              <Box sx={{ marginTop: 2 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 'bold',
                    color: '#333',
                    marginBottom: 1,
                  }}
                >
                  Please upload the reviewed mapping CSV
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  component="label"
                  sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid black',
                    boxShadow: 'none',
                  }}
                >
                  Upload File
                  <input
                    type="file"
                    hidden
                    onChange={handleFileUploadCsv}
                  />
                </Button>
                {mappingFileName && (
                  <><Typography
                    variant="body2"
                    sx={{ marginTop: 1, color: 'green', fontWeight: 'bold' }}
                  >
                    Uploaded File: {mappingFileName}
                  </Typography><Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateResult}
                    sx={{
                      marginTop: 2,
                      backgroundColor: 'white',
                      color: 'black',
                      border: '1px solid black',
                      boxShadow: 'none',
                      height: '40px',
                    }}
                  >
                      Generate DDL
                    </Button></>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {tabIndex === 2 && (
        <Box sx={{ marginTop: 2 }}>
          <Box
            sx={{
              marginTop: 4,
              padding: '16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginBottom: 2,
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              DDL
            </Typography>
            {generateApiResult?.report ? (
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
              >
                {String(generateApiResult.report)}
              </Typography>
            ) : (
              <Typography variant="body2">No result available. Please generate the migration script first.</Typography>
            )}
          </Box>
          <Box sx={{ marginTop: 2 }}>
            {generateApiResult?.links?.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownloadDDL}
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                }}
                startIcon={<Download />}
              >
                Download DDL
              </Button>
            )}
          </Box>

          <Box sx={{ marginTop: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDataInsertion}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
            >
              Click to create data insertion script
            </Button>
          </Box>
        </Box>
      )}

      {tabIndex === 3 && (
        <Box sx={{ marginTop: 2 }}>
          <Box
            sx={{
              marginTop: 4,
              padding: '16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginBottom: 2,
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Data Insertion
            </Typography>
            {dataInsertionResult?.report ? (
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
              >
                {String(dataInsertionResult.report)}
              </Typography>
            ) : (
              <Typography variant="body2">No data insertion result available.</Typography>
            )}
          </Box>
          <Box sx={{ marginTop: 2 }}>
            {dataInsertionResult?.links?.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadInsertionScripts}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<Download />}
            >
              Download Insertion Scripts
            </Button>
            )}
          </Box>
        </Box>
      )}

      <SpinnerModal
        open={spinnerMessages.open}
        progress={progress}
        inProgressMessage={spinnerMessages.inProgress}
        successMessage={spinnerMessages.success}
        onClose={spinnerMessages.onClose}
      />
    </Box>
  );
};

export default RdbmsToRdbms;